import { useState, useEffect, useRef } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Ticket, Plus, Send, Paperclip, X, FileText, Image, Download, MessageCircle, Clock, AlertCircle, CheckCircle, ChevronLeft } from 'lucide-react';
import { toast } from 'sonner';

const getBackendUrl = () => {
  if (window.location.hostname !== 'localhost') {
    return window.location.origin;
  }
  return process.env.REACT_APP_BACKEND_URL || '';
};

const BACKEND_URL = getBackendUrl();

function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [newTicket, setNewTicket] = useState({ subject: '', description: '', priority: 'medium' });

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tickets/my`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setTickets(data);
      }
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch(`${BACKEND_URL}/api/tickets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(newTicket)
      });
      if (response.ok) {
        toast.success('Ticket created successfully');
        setNewTicket({ subject: '', description: '', priority: 'medium' });
        setShowNewTicket(false);
        fetchTickets();
      }
    } catch (error) {
      toast.error('Failed to create ticket');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-accent/10 text-accent border-accent/20';
      case 'in_progress': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      case 'resolved': return 'bg-primary/10 text-primary border-primary/20';
      case 'closed': return 'bg-muted/20 text-muted-foreground border-muted/30';
      default: return 'bg-muted/20 text-muted-foreground border-muted/30';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'text-destructive';
      case 'medium': return 'text-accent';
      case 'low': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  if (selectedTicket) {
    return (
      <DashboardLayout title="Ticket Conversation">
        <TicketDetail 
          ticket={selectedTicket} 
          onBack={() => {
            setSelectedTicket(null);
            fetchTickets();
          }} 
        />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Support Tickets">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <p className="text-muted-foreground">
            Submit and track your support requests
          </p>
          <Button
            onClick={() => setShowNewTicket(true)}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Ticket
          </Button>
        </div>

        {/* New Ticket Form */}
        {showNewTicket && (
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-mono font-bold">Create New Ticket</h3>
              <button onClick={() => setShowNewTicket(false)}>
                <X className="h-5 w-5 text-muted-foreground hover:text-foreground" />
              </button>
            </div>
            <form onSubmit={handleCreateTicket} className="space-y-4">
              <div>
                <Label htmlFor="subject" className="font-mono">Subject</Label>
                <Input
                  id="subject"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                  placeholder="Brief description of your issue"
                  className="font-mono mt-1"
                  required
                />
              </div>
              <div>
                <Label htmlFor="description" className="font-mono">Description</Label>
                <Textarea
                  id="description"
                  value={newTicket.description}
                  onChange={(e) => setNewTicket({ ...newTicket, description: e.target.value })}
                  placeholder="Provide details about your issue..."
                  className="font-mono mt-1 min-h-[100px]"
                  required
                />
              </div>
              <div>
                <Label htmlFor="priority" className="font-mono">Priority</Label>
                <select
                  id="priority"
                  value={newTicket.priority}
                  onChange={(e) => setNewTicket({ ...newTicket, priority: e.target.value })}
                  className="w-full mt-1 px-3 py-2 bg-background border border-border rounded-md font-mono text-sm"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setShowNewTicket(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-primary text-primary-foreground">
                  Create Ticket
                </Button>
              </div>
            </form>
          </div>
        )}

        {/* Tickets List */}
        <div className="bg-card border border-border rounded-lg">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
            </div>
          ) : tickets.length === 0 ? (
            <div className="p-8 text-center">
              <Ticket className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No tickets yet</p>
              <p className="text-sm text-muted-foreground mt-1">Create a ticket to get support</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {tickets.map((ticket) => (
                <div
                  key={ticket.ticket_id}
                  className="p-4 hover:bg-accent/5 cursor-pointer transition-colors"
                  onClick={() => setSelectedTicket(ticket)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h4 className="font-mono font-bold">{ticket.subject}</h4>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-mono border ${getStatusColor(ticket.status)}`}>
                          {ticket.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">{ticket.description}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <MessageCircle className="h-4 w-4 text-muted-foreground" />
                      <span className="text-xs text-muted-foreground">View</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground font-mono mt-2">
                    <span className={getPriorityColor(ticket.priority)}>
                      {ticket.priority.toUpperCase()} PRIORITY
                    </span>
                    <span>Created: {new Date(ticket.created_at).toLocaleDateString()}</span>
                    <span>Updated: {new Date(ticket.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function TicketDetail({ ticket, onBack }) {
  const [messages, setMessages] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    fetchAttachments();
  }, [ticket.ticket_id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tickets/${ticket.ticket_id}/messages`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (error) {
      console.error('Failed to load messages');
    }
  };

  const fetchAttachments = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/tickets/${ticket.ticket_id}/attachments`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAttachments(data);
      }
    } catch (error) {
      console.error('Failed to load attachments');
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/tickets/${ticket.ticket_id}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: newMessage })
      });
      if (response.ok) {
        setNewMessage('');
        fetchMessages();
      }
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 
                         'application/pdf', 'application/msword', 
                         'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
                         'text/plain', 'text/csv'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('File type not allowed. Please upload images, PDFs, or documents.');
      return;
    }

    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const base64Data = reader.result.split(',')[1];
        
        const response = await fetch(`${BACKEND_URL}/api/tickets/${ticket.ticket_id}/attachments`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            filename: file.name,
            content_type: file.type,
            data: base64Data
          })
        });

        if (response.ok) {
          toast.success('File uploaded successfully');
          fetchAttachments();
        } else {
          throw new Error('Upload failed');
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadAttachment = async (attachment) => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/tickets/${ticket.ticket_id}/attachments/${attachment.attachment_id}`,
        { credentials: 'include' }
      );
      if (response.ok) {
        const data = await response.json();
        const link = document.createElement('a');
        link.href = `data:${data.content_type};base64,${data.data}`;
        link.download = data.filename;
        link.click();
      }
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'open': return <AlertCircle className="h-4 w-4 text-accent" />;
      case 'in_progress': return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'resolved': return <CheckCircle className="h-4 w-4 text-primary" />;
      default: return <Ticket className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const isImage = (contentType) => contentType?.startsWith('image/');

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={onBack} className="p-2">
          <ChevronLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            {getStatusIcon(ticket.status)}
            <h2 className="text-xl font-mono font-bold">{ticket.subject}</h2>
            <span className={`px-2 py-0.5 rounded-full text-xs font-mono border ${
              ticket.status === 'open' ? 'bg-accent/10 text-accent border-accent/20' :
              ticket.status === 'in_progress' ? 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20' :
              ticket.status === 'resolved' ? 'bg-primary/10 text-primary border-primary/20' :
              'bg-muted/20 text-muted-foreground border-muted/30'
            }`}>
              {ticket.status.replace('_', ' ').toUpperCase()}
            </span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">
            Created {new Date(ticket.created_at).toLocaleString()}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Messages Section */}
        <div className="lg:col-span-2 bg-card border border-border rounded-lg flex flex-col h-[600px]">
          <div className="p-4 border-b border-border">
            <h3 className="font-mono font-bold flex items-center gap-2">
              <MessageCircle className="h-4 w-4" />
              Conversation
            </h3>
          </div>
          
          {/* Messages List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Initial ticket description */}
            <div className="flex gap-3">
              <div className="h-8 w-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <span className="text-xs font-bold text-primary">You</span>
              </div>
              <div className="flex-1">
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
                  <p className="text-sm">{ticket.description}</p>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(ticket.created_at).toLocaleString()}
                </p>
              </div>
            </div>

            {/* Admin notes if any */}
            {ticket.admin_notes && (
              <div className="flex gap-3">
                <div className="h-8 w-8 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-bold text-secondary">Admin</span>
                </div>
                <div className="flex-1">
                  <div className="bg-secondary/5 border border-secondary/20 rounded-lg p-3">
                    <p className="text-sm">{ticket.admin_notes}</p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Admin Response</p>
                </div>
              </div>
            )}

            {/* Messages */}
            {messages.map((msg) => (
              <div key={msg.message_id} className="flex gap-3">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                  msg.user_role === 'admin' ? 'bg-secondary/20' : 'bg-primary/20'
                }`}>
                  <span className={`text-xs font-bold ${
                    msg.user_role === 'admin' ? 'text-secondary' : 'text-primary'
                  }`}>
                    {msg.user_role === 'admin' ? 'A' : 'You'}
                  </span>
                </div>
                <div className="flex-1">
                  <div className={`rounded-lg p-3 ${
                    msg.user_role === 'admin' 
                      ? 'bg-secondary/5 border border-secondary/20' 
                      : 'bg-primary/5 border border-primary/20'
                  }`}>
                    <p className="text-xs font-mono text-muted-foreground mb-1">
                      {msg.user_name} ({msg.user_role})
                    </p>
                    <p className="text-sm">{msg.content}</p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {new Date(msg.created_at).toLocaleString()}
                  </p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <div className="p-4 border-t border-border">
            <form onSubmit={handleSendMessage} className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your reply..."
                className="flex-1 font-mono"
                disabled={ticket.status === 'closed'}
              />
              <Button 
                type="submit" 
                disabled={sending || !newMessage.trim() || ticket.status === 'closed'}
                className="bg-primary text-primary-foreground"
              >
                <Send className="h-4 w-4" />
              </Button>
            </form>
            {ticket.status === 'closed' && (
              <p className="text-xs text-muted-foreground mt-2">This ticket is closed. Create a new ticket for further support.</p>
            )}
          </div>
        </div>

        {/* Attachments Section */}
        <div className="bg-card border border-border rounded-lg">
          <div className="p-4 border-b border-border">
            <h3 className="font-mono font-bold flex items-center gap-2">
              <Paperclip className="h-4 w-4" />
              Attachments
            </h3>
          </div>
          
          <div className="p-4">
            {/* Upload Button */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="hidden"
              accept="image/*,.pdf,.doc,.docx,.txt,.csv"
            />
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              className="w-full mb-4"
              disabled={uploading || ticket.status === 'closed'}
            >
              {uploading ? (
                <>
                  <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-primary border-r-transparent" />
                  Uploading...
                </>
              ) : (
                <>
                  <Paperclip className="h-4 w-4 mr-2" />
                  Upload File
                </>
              )}
            </Button>
            <p className="text-xs text-muted-foreground mb-4">
              Max 5MB. Supported: Images, PDF, DOC, TXT, CSV
            </p>

            {/* Attachments List */}
            {attachments.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">No attachments yet</p>
              </div>
            ) : (
              <div className="space-y-2">
                {attachments.map((att) => (
                  <div
                    key={att.attachment_id}
                    className="flex items-center gap-3 p-3 bg-background border border-border rounded-md hover:border-primary/50 cursor-pointer transition-colors"
                    onClick={() => downloadAttachment(att)}
                  >
                    {isImage(att.content_type) ? (
                      <Image className="h-8 w-8 text-accent flex-shrink-0" />
                    ) : (
                      <FileText className="h-8 w-8 text-primary flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-mono truncate">{att.filename}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(att.size)} • {new Date(att.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <Download className="h-4 w-4 text-muted-foreground" />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Tickets;
